import { useLocalStorageState } from 'hooks/useLocalStorageState';
import React, { ReactNode } from 'react';
import { DockableLayout, DockableLayoutProps } from './DockableLayout';
import { DockMode } from './useDockableLayout';

export interface DockableLayoutWithStorageProps
  extends Omit<
    DockableLayoutProps,
    'mode' | 'onModeChange' | 'width' | 'onWidthChange'
  > {
  /**
   * Content to show in the dockable panel (left side)
   */
  panel: DockableLayoutProps['panel'];

  /**
   * Main content area
   */
  children: ReactNode;

  /**
   * LocalStorage key prefix for persisting sidebar state
   * Will create keys: `${storageKey}:mode` and `${storageKey}:width`
   * @default 'v5-sidebar'
   */
  storageKey?: string;

  /**
   * Initial dock mode (only used on first render if no stored value)
   * @default 'docked'
   */
  defaultMode?: DockMode;

  /**
   * Initial width (only used on first render if no stored value)
   * @default 256
   */
  defaultWidth?: number;
}

/**
 * DockableLayout with localStorage persistence
 *
 * Automatically persists and restores:
 * - Collapsed/docked state
 * - Sidebar width
 *
 * Uses localStorage as single source of truth for sidebar state.
 */
export const DockableLayoutWithStorage = React.forwardRef<
  HTMLDivElement,
  DockableLayoutWithStorageProps
>(
  (
    {
      panel,
      children,
      storageKey = 'v5-sidebar',
      defaultMode = 'docked',
      defaultWidth = 268,
      ...props
    },
    ref
  ) => {
    // Persist collapsed state in localStorage
    const [mode, setMode] = useLocalStorageState<DockMode>(
      `${storageKey}:mode`,
      defaultMode
    );

    // Persist width in localStorage
    const [width, setWidth] = useLocalStorageState<number>(
      `${storageKey}:width`,
      defaultWidth
    );

    return (
      <DockableLayout
        ref={ref}
        panel={panel}
        mode={mode}
        onModeChange={setMode}
        width={width}
        onWidthChange={setWidth}
        {...props}
      >
        {children}
      </DockableLayout>
    );
  }
);

DockableLayoutWithStorage.displayName = 'DockableLayoutWithStorage';
